import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { fetchEnumValues } from '../services/metaInfoApi';
import { referenceTableModeFor } from '../utils/effectiveLookupDeclaration';
import { useMetaInfo } from './useMetaInfo';
import { findWithSchemaFold } from '../utils/metaInfoSchemaMatch';
import type {
  EnumLookupFetchTarget,
  EnumValueCache,
  EnumValueSource,
} from '../../../../types/electron';

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * How old a **lookup** pair cache may get before an open picker / cell editor
 * refreshes it in the background (stale-while-revalidate).
 *
 * Lives here rather than in `utils/lookupLabelColumn.ts` because the staleness
 * decision is made *inside this hook* — it is the only place that can see the
 * cache entry, the resolved target and the caller's `active` signal at once.
 * (Same rule as `LOOKUP_TARGET_COLUMNS_STALE_MS`, which sits next to the fetch
 * it bounds.)
 *
 * Only `source: 'lookup'` caches revalidate: their rows live in another table
 * that anyone can edit, and re-reading them is a bounded ≤201-row select.
 * Sampled / native / check caches keep their manual-only lifecycle.
 */
export const LOOKUP_ENUM_STALE_MS = 15 * 60 * 1000;

export const enumValuesQueryKey = (
  connectionId: string | null,
  database: string | null,
  schema: string,
  table: string,
  column: string,
  path?: string,
) =>
  [
    'enum-values',
    connectionId,
    database,
    schema,
    table,
    column,
    path ?? '',
  ] as const;

export type UseEnumValuesArgs = {
  connectionId: string | null;
  database: string | null;
  schema: string;
  table: string;
  column: string;
  engine: string;
  /** Initial source preference when first fetching. Defaults to `'auto'`. */
  source?: EnumValueSource | 'auto';
  /**
   * When false (the default), the hook is *inert* — it does not start a
   * network call. The picker / declare dialog opts in explicitly by passing
   * `enabled: true` once the user has confirmed a scan.
   */
  enabled?: boolean;
  /**
   * "The surface reading these values is OPEN on this column right now" — the
   * picker feeds its popover-open state, the cell editor feeds `true` once its
   * gates pass. `enabled` cannot stand in for it: the picker arms `enabled`
   * only on a cache MISS, so an open picker over a cache HIT (the case
   * stale-while-revalidate exists for) never sets it.
   *
   * The caller must keep this `false` until a lookup column's label column has
   * resolved — revalidating before then would cache keys with no labels.
   */
  active?: boolean;
  /** JSONPath within the JSON column; absent for column-level enum pickers. */
  path?: string;
  pathKind?: 'scalar' | 'array';
  /**
   * Required when `source === 'lookup'` — the FK target the key/label pairs
   * are read from. `labelColumn` is resolved live by the caller (from
   * `tableDisplayColumns`); null/absent = keys-only fetch.
   */
  lookup?: EnumLookupFetchTarget;
};

/** One key/label pair from a lookup enum, aligned with `values[i]`. */
export type EnumValueEntry = { value: string; label: string | null };

/** Exported for the schema-fold unit test — see `useEnumValues.test.ts`. */
export const findCacheEntry = (
  entries: EnumValueCache[] | undefined,
  schema: string,
  table: string,
  column: string,
  path?: string,
): EnumValueCache | undefined =>
  findWithSchemaFold(
    entries,
    schema,
    (e) =>
      e.table === table &&
      e.column === column &&
      (e.path ?? undefined) === (path ?? undefined),
  );

/**
 * True when a lookup cache entry is older than the SWR window. An unparseable
 * (or missing) `sampledAt` counts as FRESH: an unknown age is not evidence of
 * staleness, and treating it as stale would revalidate on every open forever.
 */
const isStaleLookupCache = (entry: EnumValueCache | undefined): boolean => {
  if (!entry) return false;
  const sampledAt = Date.parse(entry.sampledAt ?? '');
  if (Number.isNaN(sampledAt)) return false;
  return Date.now() - sampledAt > LOOKUP_ENUM_STALE_MS;
};

/**
 * Cache-first hook for enum value lists.
 *
 * - If a cache entry exists in MetaInfo, the hook resolves synchronously with
 *   the cached values and `isCacheHit = true`. No IPC fires until the caller
 *   invokes `refetch()` — opt-in observation.
 * - If no cache entry exists, the hook stays in a `'idle'` state until the
 *   caller sets `enabled: true`. Then it fires the IPC, persists the result
 *   to the cache (the IPC does this server-side), and returns the values.
 * - `refetch()` always re-fires the IPC, regardless of the cache state.
 * - `active` (the surface is open on this column) additionally enables
 *   stale-while-revalidate for **lookup** caches: cached pairs render
 *   instantly and one background refresh runs per open cycle when the entry is
 *   older than `LOOKUP_ENUM_STALE_MS`. Every other source stays manual-only.
 */
export function useEnumValues(args: UseEnumValuesArgs) {
  const {
    connectionId,
    database,
    schema,
    table,
    column,
    engine,
    source = 'auto',
    enabled = false,
    active = false,
    path,
    pathKind,
    lookup,
  } = args;

  const qc = useQueryClient();
  const { track } = useAnalytics();
  const { data: metaInfo } = useMetaInfo(connectionId, database);
  const cacheEntry = findCacheEntry(metaInfo?.enumValueCache, schema, table, column, path);
  const isCacheHit = Boolean(cacheEntry);

  /**
   * The target table is flagged `'on-demand'`: its pairs are resolved per
   * visible grid window (`useOnDemandLookupLabels`) and never persisted. A
   * pair fetch here would be wrong twice over — truncated by design (the
   * target is too big to enumerate, which is why it was flagged) and a
   * write-through to a cache the user asked never to keep.
   *
   * The guard lives in the hook rather than at each call site so ALL of them —
   * the filter picker, the cell editor, the declare dialog, anything added
   * later — are covered by one decision. The surfaces are also excluded
   * upstream (the picker slot, the grids' declared-enum sets); this is the
   * floor under those. It is enforced TWICE, on `enabled` and inside
   * `queryFn`, because `refetch()` bypasses `enabled` by design.
   *
   * Whatever is already cached still RENDERS: the flag stops the fetch, it
   * does not blank a surface showing perfectly good pairs.
   */
  const isOnDemandTarget =
    source === 'lookup' && referenceTableModeFor(metaInfo, lookup) === 'on-demand';

  const query = useQuery<{
    values: string[];
    source: EnumValueSource;
    truncated: boolean;
    sampledRows?: number;
    /** Aligned with `values`; present only for source 'lookup'. */
    labels?: (string | null)[];
  }>({
    queryKey: enumValuesQueryKey(connectionId, database, schema, table, column, path),
    queryFn: async () => {
      // The `enabled` flag below stops the AUTOMATIC fetch; `refetch()`
      // deliberately ignores `enabled`, so the guard has to stand here too or
      // a Refresh button is a hole straight through it. Throwing rather than
      // returning empty keeps `query.data` as it was, so cached pairs go on
      // rendering — the flag stops the fetch, it never blanks a surface.
      if (isOnDemandTarget) {
        throw new Error(
          'Lookup pairs are resolved on demand for this target and are never cached.',
        );
      }
      if (!connectionId || !database) {
        return { values: [], source: 'sampled' as const, truncated: false };
      }
      const result = await fetchEnumValues(
        connectionId,
        database,
        schema,
        table,
        column,
        engine,
        source,
        path,
        pathKind,
        lookup,
      );
      // The IPC writes through to MetaInfo's enumValueCache; invalidate the
      // metaInfo query so subscribers re-read the freshly cached entry.
      qc.invalidateQueries({
        queryKey: ['metainfo', connectionId, database],
      });
      return result;
    },
    // Seed the query from the cache so the first read is synchronous when a
    // MetaInfo entry already exists. TanStack treats `initialData` as fresh
    // for `staleTime`, which avoids an immediate background refetch on mount.
    initialData: cacheEntry
      ? {
          values: cacheEntry.values,
          source: cacheEntry.source,
          truncated: cacheEntry.truncated,
          ...(cacheEntry.sampledRows !== undefined
            ? { sampledRows: cacheEntry.sampledRows }
            : {}),
          ...(cacheEntry.labels ? { labels: cacheEntry.labels } : {}),
        }
      : undefined,
    enabled: Boolean(connectionId && database && enabled) && !isOnDemandTarget,
    staleTime: FIVE_MINUTES,
  });

  // --- Stale-while-revalidate (lookup caches only) -----------------------
  //
  // A lookup cache's rows live in ANOTHER table, so it can go stale without
  // anything happening on this column. When a surface opens over a cache older
  // than `LOOKUP_ENUM_STALE_MS`, the cached pairs still render instantly (the
  // read below is untouched, `isLoading` stays false) and one refresh runs
  // behind them; the write-through inside `fetchEnumValues` persists the new
  // pairs, so the next open is a plain, fresh cache hit.
  const swrDue =
    active &&
    source === 'lookup' &&
    !isOnDemandTarget &&
    // No resolved target ⇒ the caller has not finished resolving the FK /
    // label column: fetching now would cache keys with no labels.
    Boolean(lookup) &&
    Boolean(connectionId && database) &&
    cacheEntry?.source === 'lookup' &&
    isStaleLookupCache(cacheEntry);

  // One revalidation per OPEN CYCLE. The refetch does refresh `sampledAt`, but
  // that lands via a MetaInfo round-trip — several renders later — so the ref
  // (not the timestamp) is what keeps an open picker from looping.
  const swrFiredRef = useRef(false);
  const { refetch } = query;
  useEffect(() => {
    if (!active) swrFiredRef.current = false;
  }, [active]);
  useEffect(() => {
    if (!swrDue || swrFiredRef.current) return;
    swrFiredRef.current = true;
    void (async () => {
      try {
        const result = await refetch();
        if (result.data?.source === 'lookup') {
          // Distinguishes this from the explicit Refresh buttons ('manual'),
          // the grid's miss-driven self-heal ('miss') and the post-edit
          // write-through ('write-through').
          track('feature:enum_values_refresh', { engine, source: 'lookup', reason: 'swr' });
        }
      } catch {
        // A dropped target table or a lost session must never break the
        // surface that is showing perfectly good cached pairs.
      }
    })();
  }, [swrDue, refetch, engine, track]);

  // `initialData` only seeds the query on its FIRST observation. If this hook
  // mounts before the MetaInfo query has the cache entry (e.g. right after an
  // import / `invalidateQueries(['metainfo'])`, or a background refresh), the
  // `enum-values` query is created empty; when the cache entry later appears,
  // `initialData` is ignored (the query already exists) and no fetch is armed
  // (the picker's lazy-enable skips once `isCacheHit` is true) — leaving the
  // picker permanently empty. Fall back to the live `cacheEntry` so a cache
  // hit is reactive, not first-render-only. A completed fetch (`query.data`)
  // still wins so `refetch()` / Refresh reflects fresh sampled values.
  const values = query.data?.values ?? cacheEntry?.values ?? [];
  // Labels exist only for the 'lookup' tier. Unlike the fields above this
  // reads the *presence* of `query.data` rather than `??`-ing the field: a
  // completed non-lookup fetch legitimately carries no labels, and falling
  // through to the cache entry there would re-attach stale labels to a fresh
  // value list (the IPC's write-through has already dropped them on disk).
  const labels: (string | null)[] | undefined = query.data
    ? query.data.labels
    : cacheEntry?.labels;
  // Convenience pairing for label-rendering surfaces (picker, cell editor,
  // grid label maps). Undefined whenever there are no labels, so callers can
  // branch on lookup vs plain enum with one check.
  const entries: EnumValueEntry[] | undefined = useMemo(
    () =>
      labels
        ? values.map((value, i) => ({ value, label: labels[i] ?? null }))
        : undefined,
    [values, labels],
  );

  return {
    /** Final value-list to render. Empty array on cache miss + enabled=false. */
    values,
    source: query.data?.source ?? cacheEntry?.source,
    truncated: query.data?.truncated ?? cacheEntry?.truncated ?? false,
    sampledRows: query.data?.sampledRows ?? cacheEntry?.sampledRows,
    /** Labels aligned with `values`; only lookup-source results carry them. */
    labels,
    /** `values` zipped with `labels`; undefined when there are no labels. */
    entries,
    isCacheHit,
    isLoading: query.isFetching && !isCacheHit,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export type UseEnumValuesReturn = ReturnType<typeof useEnumValues>;
